/**
 * screens/admin/adminRealmMismatch.tsx — N-4 · Realm mismatch events.
 *
 * INTERNAL CONSOLE ONLY. SKPLATFORM-UICC-006 §8 / DB-006 S6.
 *
 * Every row here is a token that was CRYPTOGRAPHICALLY VALID and arrived in
 * the wrong realm. That is a candidate security incident, not a log line — it
 * is the SKBACKBONE-CC-001 failure class, where the token was structurally
 * valid and the authorisation was wrong.
 *
 * Malformed, unsigned and expired tokens are NOT recorded, so bot pressure
 * cannot flood this table and every row is high-signal.
 *
 * ── TWO DESIGN RULES ──────────────────────────────────────────────────────
 *
 * 1. THE REALM PAIR IS THE HEADLINE, and the two directions mean different
 *    things, so they get different treatments rather than one generic
 *    "mismatch" chip:
 *      customer → staff : a customer token reaching a staff surface. Someone
 *                         is presenting a Passport token at Platform.
 *      staff → customer : a staff token reaching a customer surface. This is
 *                         the impersonation-reaches-Passport case, the one
 *                         P-D8 exists to prevent.
 * 2. ZERO ROWS IS THE EXPECTED STATE, and the empty state says so
 *    AFFIRMATIVELY. On this screen empty is GOOD NEWS and should read as good
 *    news — not as the usual apologetic "nothing to show".
 *
 * No row actions: this is an immutable ledger.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { SettingsHeader, Panel, DataTable, Tag, type Column } from '../../components/platform/primitives';
import { FS, FW } from '../../utils/platformTokens';
import { listRealmMismatches } from '../../services/platform/platformApi';
import type { RealmMismatchRow } from '../../schema/platform/types';

const AdminRealmMismatch: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [rows, setRows] = useState<RealmMismatchRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try { setRows(await listRealmMismatches(24).catch(() => [])); }
      finally { setLoading(false); }
    })();
  }, []);

  const columns: Column<RealmMismatchRow>[] = [
    {
      key: 'when',
      header: 'Time',
      width: 165,
      render: (r) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {new Date(r.occurred_at).toISOString().slice(0, 19).replace('T', ' ')}
        </Text>
      ),
    },
    {
      /* The headline. An actual arrow, in accent, and a distinct treatment per
         direction — the two are not the same event. */
      key: 'pair',
      header: 'Presented → expected',
      width: 250,
      render: (r) => {
        const toCustomer = r.expected_realm === 'customer';
        return (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Tag tone={toCustomer ? 'danger' : 'warn'}>{r.presented_realm}</Tag>
            <Text style={{ color: c.accent, fontSize: FS.md, fontWeight: FW.semibold }}>→</Text>
            <Tag tone="neutral">{r.expected_realm}</Tag>
          </View>
        );
      },
    },
    {
      key: 'endpoint',
      header: 'Endpoint',
      render: (r) => (
        <Text selectable style={{ fontSize: FS.xs, color: c.ink2, fontFamily: theme.font.mono }}>
          {r.endpoint}
        </Text>
      ),
    },
    {
      key: 'issuer',
      header: 'Issuer',
      width: 210,
      render: (r) => (
        <Text selectable style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {r.token_issuer}
        </Text>
      ),
    },
    {
      key: 'kid',
      header: 'kid',
      width: 150,
      render: (r) => (
        <Text selectable style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {r.token_kid ?? '—'}
        </Text>
      ),
    },
    {
      key: 'aud',
      header: 'Audience',
      width: 130,
      render: (r) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {r.token_audience ?? '—'}
        </Text>
      ),
    },
    {
      key: 'ip',
      header: 'Source IP',
      width: 140,
      render: (r) => (
        <Text selectable style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {r.source_ip ?? '—'}
        </Text>
      ),
    },
  ];

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1400, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Realm mismatch events"
        sub="Signature-valid tokens that arrived in the wrong realm. Last 24 hours, newest first — Softknack internal."
      />

      {/* Non-zero leads with the count, in accent, large, above the table:
          this is the number somebody needs to see before the detail. */}
      {rows.length > 0 && (
        <View style={{ marginBottom: 18 }}>
          <Text style={{ fontSize: 34, fontWeight: FW.semibold, color: c.accent, fontFamily: theme.font.mono }}>
            {rows.length.toLocaleString()}
          </Text>
          <Text style={{ fontSize: FS.sm, color: c.ink2 }}>
            {rows.length === 1 ? 'mismatch' : 'mismatches'} in the last 24 hours. Each is a valid
            token that reached a surface it was never minted for.
          </Text>
        </View>
      )}

      <Panel>
        <DataTable
          columns={columns}
          rows={rows}
          keyExtractor={(r) => r.id}
          minWidth={1250}
          empty={
            /*
              Empty is GOOD NEWS here, and it reads that way. Deliberately not
              "Nothing to show" — on this screen an empty table is the system
              working, and the copy should say so affirmatively.
            */
            <View style={{ paddingVertical: 26, alignItems: 'center', gap: 6 }}>
              <Text style={{ fontSize: FS.md, color: c.success, fontWeight: FW.medium }}>
                {loading ? 'Checking…' : 'No realm mismatches in this period'}
              </Text>
              {!loading && (
                <Text style={{ fontSize: FS.sm, color: c.ink3, textAlign: 'center', maxWidth: 560, lineHeight: 19 }}>
                  Every token presented in the last 24 hours reached the realm it was minted for.
                  This is the expected state.
                </Text>
              )}
            </View>
          }
        />
      </Panel>
    </ScrollView>
  );
};

export default AdminRealmMismatch;
